import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import {
  MODULE,
  SUB_MODULE,
  CURRICULUM_TOPIC,
  ACADEMIC_YEAR,
  APPLICABLE_GRADES,
  ACTIVITY_TYPE,
  TOPIC_TLM,
  DESCRIPTION,
  KIT_TYPES_OPTIONS,
  KIT_NAME,
  KIT_TYPE
} from "../consts";
import { routes } from "@/routes/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import { useGetModulesQuery } from "@/services/masterData/curriculum/useGetModulesQuery";
import { useGetSubmodulesQuery } from "@/services/masterData/curriculum/useGetSubmodulesQuery";
import { useGetApplicableGradesQuery } from "@/services/masterData/curriculum/useGetApplicableGradesQuery";
import { ACTIVITY_TYPES } from "../consts";
import { toast } from "sonner";
import { useState, useEffect, useRef } from "react";
import { useAddCurriculumMasterMutation } from "@/services/masterData";
import {
  useGetCurriculumMasterDetailQuery,
  useUpdateCurriculumMasterMutation,
} from "@/services/masterData";
import useTlmMasterQuery from "@/services/masterData/tlm-master/useGetTlmMasterMutation";
import { useGetActiveTlmMaster } from "@/services/masterData/tlm-master/useGetActiveTlmMaster";
import { useQueryClient } from "@tanstack/react-query";

const useAddEdit = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const isView = action == routes.view;
  const isEdit = action == routes.edit;
  const isMobile = useIsMobile();
  const [selectedModuleId, setSelectedModuleId] = useState<string>("");
  const queryClient = useQueryClient();
  const [initialDetailPayload, setInitialDetailPayload] = useState<Record<
    string,
    any
  > | null>(null);
  const initializedFromDetailRef = useRef<boolean>(false);

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [MODULE]: "",
      [SUB_MODULE]: "",
      [CURRICULUM_TOPIC]: "",
      [ACADEMIC_YEAR]: new Date().getFullYear(),
      [APPLICABLE_GRADES]: [],
      [ACTIVITY_TYPE]: "",
      [TOPIC_TLM]: "",
      [DESCRIPTION]: "",
    },
  });

  // Fetch modules
  const { data: tlmRes, isPending: tlmLoading } = useGetActiveTlmMaster();

  // Fetch modules
  const {
    data: modulesRes,
    isPending: isModulesLoading,
    error: modulesError,
  } = useGetModulesQuery();

  // Fetch submodules based on selected module
  const {
    data: submodulesRes,
    isPending: isSubmodulesLoading,
    error: submodulesError,
  } = useGetSubmodulesQuery(selectedModuleId, String(form.watch(APPLICABLE_GRADES)));

  // Fetch applicable grades
  const {
    data: gradesRes,
    isPending: isGradesLoading,
    error: gradesError,
  } = useGetApplicableGradesQuery();

  const { mutateAsync: addCurriculum, isPending } =
    useAddCurriculumMasterMutation({
      onSuccess: () => {
        toast.success("Curriculum created successfully");
        onBackClick();
      },
      onError: (error: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error.response.data.message
            : "Failed to create curriculum"
        );
      },
    });

  const { mutateAsync: updateCurriculum, isPending: isUpdating } =
    useUpdateCurriculumMasterMutation({
      onSuccess: () => {
        toast.success("Curriculum updated successfully");
        onBackClick();
      },
      onError: (error: any) => {
        toast.error(
          error.response.data?.issues?.message || error.response.data.message
            || "Failed to update curriculum"
        );
      },
    });

  const requiredFields = getRequiredFields(schema);

  // Fetch detail when editing or viewing
  const { data: detailRes, isPending: isDetailLoading } =
    useGetCurriculumMasterDetailQuery(
      isEdit || isView ? String(id) : undefined
    );

  useEffect(() => {
    if ((isEdit || isView) && detailRes?.data) {
      const d = detailRes.data[0];
      console.log({d});
      
      form.reset({
        [MODULE]: String(d?.moduleId ?? ""),
        [SUB_MODULE]: String(d?.subModuleId ?? ""),
        [CURRICULUM_TOPIC]: String(d?.curriculumTopic ?? ""),
        [ACADEMIC_YEAR]: Number(d?.academicYear ?? new Date().getFullYear()),
        [APPLICABLE_GRADES]: (d?.applicableGrades as string[]) || [],
        [ACTIVITY_TYPE]: String(d?.activityType ?? ""),
        [TOPIC_TLM]: d?.topicTLM ? String(d?.topicTLM) : "",
        [DESCRIPTION]: d?.description ? String(d?.description) : "",
      });
      // Store normalized initial payload for diffing on update
      setInitialDetailPayload({
        moduleId: String(d?.moduleId ?? ""),
        subModuleId: String(d?.subModuleId ?? ""),
        curriculumTopic: String(d?.curriculumTopic ?? ""),
        academicYear: Number(d?.academicYear ?? new Date().getFullYear()),
        applicableGrades: (d?.applicableGrades as string[]) || [],
        activityType: String(d?.activityType ?? ""),
        topicTLM: d?.topicTLM ? String(d?.topicTLM) : undefined,
        description: d?.description ? String(d?.description) : undefined,
      });
      if (d?.moduleId) {
        setSelectedModuleId(String(d.moduleId));
      }
      initializedFromDetailRef.current = true;
    }
  }, [detailRes, isEdit, isView, form]);

  // Watch module selection to fetch submodules
  const watchedModuleId = form.watch(MODULE);
  useEffect(() => {
    if (watchedModuleId) {
      setSelectedModuleId(watchedModuleId);
      // Reset submodule when module changes by user, but not during initial detail load
      const initialModuleId = String(initialDetailPayload?.moduleId ?? "");
      const isSameAsInitial = String(watchedModuleId) === initialModuleId;
      const shouldReset = !initializedFromDetailRef.current || !isSameAsInitial;
      if (shouldReset && !isView) {
        form.setValue(SUB_MODULE, "");
      }
    }
      console.log("szsz",watchedModuleId);  
  }, [watchedModuleId, form, initialDetailPayload, isView]);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}`);
  };

  const onSubmit = async (data: formValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);
    const payload = {
      moduleId: String(_data[MODULE] ?? ""),
      subModuleId: ((_data[SUB_MODULE] as any)?.split(",")) || [],
      curriculumTopic: String(_data[CURRICULUM_TOPIC] ?? ""),
      academicYear: Number(_data[ACADEMIC_YEAR] ?? new Date().getFullYear()),
      applicableGrades: (_data[APPLICABLE_GRADES] as string[]) || [],
      activityType: String(_data[ACTIVITY_TYPE] ?? ""),
      topicTLM: _data[TOPIC_TLM] ? String(_data[TOPIC_TLM]) : undefined,
      description: _data[DESCRIPTION] ? String(_data[DESCRIPTION]) : undefined,
    };

    if (isEdit && id) {
      // Build diff to send only changed fields
      const diff: Record<string, any> = {};
      const original = initialDetailPayload || {};
      const keys = Object.keys(payload) as Array<keyof typeof payload>;
      for (const key of keys) {
        const newVal = payload[key];
        const oldVal = original[key];
        const bothArrays = Array.isArray(newVal) && Array.isArray(oldVal);
        const equal = bothArrays
          ? JSON.stringify(newVal) === JSON.stringify(oldVal)
          : newVal === oldVal;
        if (!equal) {
          diff[key as string] = newVal;
        }
      }

      if (Object.keys(diff).length === 0) {
        toast.info("No changes to update");
        return;
      }

      await updateCurriculum({ id: String(id), data: diff });
    } else {
      await addCurriculum({ data: payload });
    }
    queryClient.invalidateQueries({ queryKey: ["curriculumMaster"] });
  };
  // Prepare options for dropdowns
  const moduleOptions = (modulesRes?.data?.[0]?.records || []).map(
    (m: any) => ({
      label: m?.module_name,
      value: String(m?.module_id),
    })
  );

  const tlmTopicOptions = (tlmRes?.data || []).map((m: any) => ({
    label: m?.name,
    value: String(m?.tlmMasterId),
  }));

  const submoduleOptions = (submodulesRes?.data?.[0]?.records || []).map(
    (s: any) => ({
      label: `${s?.sub_module_name} (${s?.curriculum_grade})`,
      value: String(s?.sub_module_id),
    })
  );

  const gradeOptions = (gradesRes?.data || []).map((g: any) => ({
    label: g?.curriculum_grade,
    value: String(g?.curriculum_id),
  }));

  const activityTypeOptions = Object.values(ACTIVITY_TYPES).map((type) => ({
    label: type,
    value: type,
  }));

  const kitTypeOptions = Object.values(KIT_TYPES_OPTIONS).map((type) => ({
    label: type,
    value: type,
  }));

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    isLoading: isPending || isUpdating || isDetailLoading || submoduleOptions.length === 0,
    moduleOptions,
    submoduleOptions,
    gradeOptions,
    tlmTopicOptions,
    activityTypeOptions,
    kitTypeOptions,
    isModulesLoading,
    isSubmodulesLoading,
    isGradesLoading,
    tlmLoading,
  };
};

export default useAddEdit;